import { Flex } from "@/components/atoms/flex";
import { Agenda } from "@microsoft/mgt-react";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { CustomAgendaEventTemplate } from "./CustomAgendaEventTemplate";
import { CustomAgendaDateTemplate } from "./CustomAgendaDateTemplate";
import { getUserScheduleView } from "@/repository/queryFns/teams";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { useAgendaStyle } from "./styles";
import { AgendaContentPopoverProps } from "./types";

export const AgendaContentPopover = (props: AgendaContentPopoverProps) => {
	const intl = useIntl();
	const { userPrincipalName } = props;
	const [events, setEvents] = useState([] as any[]);
	const [isNotSharedError, setIsNotSharedError] = useState(false);
	const areContactsAgendaEventsHidden = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.areContactsAgendaEventsHidden
	);
	const classes = useAgendaStyle();

	const CalendarNotSharedErrorCode = "5037";

	React.useEffect(() => {
		initialize();
	}, []);

	const initialize = () => {
		getUserAgenda();
	};

	const getInitialDateData = (event: any) => {
		const startDate = new Date(`${event.start.dateTime}Z`);
		const endDate = new Date(`${event.end.dateTime}Z`);
		const initialStartMonth = startDate.getMonth() + 1;
		const initialStartDay = startDate.getDate();
		const endMonth = endDate.getMonth() + 1;
		const endDay = endDate.getDate();

		return {
			startDate: startDate,
			endDate: endDate,
			initialStartMonth: initialStartMonth,
			initialStartDay: initialStartDay,
			endMonth: endMonth,
			endDay: endDay,
		};
	};

	const getStartDateData = (initialDateData: any) => {
		return {
			startDate: initialDateData.startDate,
			startMonth: initialDateData.initialStartMonth,
			startDay: initialDateData.initialStartDay,
		};
	};

	const getNextDayData = (
		startDate: Date,
		startMonth: number,
		startDay: number
	) => {
		startDate.setDate(startDate.getDate() + 1);
		startMonth = startDate.getMonth() + 1;
		startDay = startDate.getDate();

		return {
			startDate: startDate,
			startMonth: startMonth,
			startDay: startDay,
		};
	};

	const isStartDayDifferentThanEndDate = (
		startDayData: any,
		initialDateData: any
	) => {
		return (
			startDayData.startMonth !== initialDateData.endMonth ||
			startDayData.startDay !== initialDateData.endDay
		);
	};

	const isEventAlreadyInList = (startDayData: any, initialDateData: any) => {
		return (
			initialDateData.initialStartMonth === startDayData.startMonth &&
			initialDateData.initialStartDay === startDayData.startDay
		);
	};

	const getCurrentDayAgendaEvent = (startDayData: any, event: any) => {
		const agendaEventCopy = JSON.parse(JSON.stringify(event)) as any;
		agendaEventCopy.id = crypto.randomUUID();
		// Express local midnight as UTC so MGT (which treats dateTime as UTC) renders the correct local day
		const localMidnight = new Date(startDayData.startDate);
		localMidnight.setHours(0, 0, 0, 0);
		agendaEventCopy.start.dateTime = localMidnight
			.toISOString()
			.replace("Z", "")
			.replace(/\.\d{3}$/, ".0000000");

		return agendaEventCopy;
	};

	const isAllDayEvent = (event: any) => {
		const endDate: Date = new Date(`${event?.end?.dateTime}Z`);
		const startDate: Date = new Date(`${event?.start?.dateTime}Z`);

		const millisecondsInADay = 1000 * 60 * 60 * 24;
		const diffMs = endDate.getTime() - startDate.getTime();

		return (
			diffMs > 0 &&
			diffMs % millisecondsInADay === 0 &&
			startDate.getUTCHours() === 0 &&
			startDate.getUTCMinutes() === 0 &&
			startDate.getUTCSeconds() === 0
		);
	};

	const processAllDayEvents = (eventsList: any) => {
		return eventsList.map((event: any) => ({
			...event,
			isAllDay: isAllDayEvent(event),
		}));
	};

	const mapEventsToAgenda = (eventsList: any) => {
		return eventsList.map((item: any, index: number) => ({
			id: index.toString(),
			subject: item.subject || intl.formatMessage({ id: "Agenda.Busy" }),
			start: item.start,
			end: item.end,
			location: {
				displayName: item.location?.displayName || "",
			},
			isAllDay: item.isAllDay,
		}));
	};

	const isMultiDayEvent = (event: any) => {
		const start = new Date(`${event.start.dateTime}Z`);
		const end = new Date(`${event.end.dateTime}Z`);
		
		return (
			start.getFullYear() !== end.getFullYear() ||
			start.getMonth() !== end.getMonth() ||
			start.getDate() !== end.getDate()
		);
	};

	const processMultipleDayEvents = (eventsList: any) => {
		let addedEventsArray: any[] = [];

		eventsList
			.filter((y: any) => isMultiDayEvent(y))
			.forEach((x: any) => {
				const initialDateData = getInitialDateData(x);

				let startDayData = getStartDateData(initialDateData);

				while (isStartDayDifferentThanEndDate(startDayData, initialDateData)) {
					if (isEventAlreadyInList(startDayData, initialDateData)) {
						startDayData = getNextDayData(
							initialDateData.startDate,
							startDayData.startMonth,
							startDayData.startDay
						);

						continue;
					}

					addedEventsArray = [
						...addedEventsArray,
						getCurrentDayAgendaEvent(startDayData, x),
					];

					startDayData = getNextDayData(
						initialDateData.startDate,
						startDayData.startMonth,
						startDayData.startDay
					);
				}
			});

		return eventsList.concat(addedEventsArray);
	};

	const getUserAgenda = () => {
		if (userPrincipalName) {
			setIsNotSharedError(false);
			getUserScheduleView(userPrincipalName).then((response: any) => {
				if (
					response?.value[0]?.error?.responseCode === CalendarNotSharedErrorCode
				) {
					setIsNotSharedError(true);
					return;
				}

				if (response?.value[0]?.scheduleItems) {
					let eventsList = response?.value[0]?.scheduleItems;

					eventsList = processAllDayEvents(eventsList);
					eventsList = processMultipleDayEvents(eventsList);
					eventsList.sort((a: any, b: any) =>
						new Date(`${a.start.dateTime}Z`).getTime() - new Date(`${b.start.dateTime}Z`).getTime()
					);

					if (areContactsAgendaEventsHidden) {
						eventsList.forEach((x: any) => {
							x.subject = intl.formatMessage({ id: "Agenda.Busy" });
							x.locations = [];
							x.location = {};
						});
					} else {
						eventsList.forEach((x: any) => {
							if (x.isPrivate === true) {
								x.subject = intl.formatMessage({ id: "Agenda.Private" });
								x.locations = [];
								x.location = {};
							}
						});
					}

					setEvents(mapEventsToAgenda(eventsList));
				}
			});
		}
	};

	const getAgendaView = () => {
		return (
			<Agenda groupByDay events={events}>
				<CustomAgendaDateTemplate template="header" intl={intl} />
				<CustomAgendaEventTemplate template="event" intl={intl} />
			</Agenda>
		);
	};

	return (
		<Flex column className={classes.agendaContentContainer}>
			{events.length > 0 ? (
				getAgendaView()
			) : (
				<div className={classes.noAgendaFound}>
					{isNotSharedError
						? intl.formatMessage({ id: "Agenda.NotSharedError" })
						: intl.formatMessage({ id: "Agenda.NoEventFound" })}
				</div>
			)}
		</Flex>
	);
};
